/*
 * ConfirmModal — custom confirmation dialog replacing window.confirm().
 * Provides a branded, accessible modal with customizable title, message,
 * and action labels.
 *
 * Usage:
 *   import { useConfirm } from "./ConfirmModal";
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Reset Routing Rules?",
 *     message: "This clears all staged and applied priorities.",
 *     confirmLabel: "Reset",
 *     variant: "danger",  // "danger" | "primary"
 *   });
 *   if (ok) { ... }
 *
 * ! file components/ConfirmModal.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
}

export default function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, message, confirmLabel, variant, resolve }
  const confirmBtnRef = useRef(null);
  const backdropRef = useRef(null);

  const confirm = useCallback(
    ({ title = "Confirm", message = "", confirmLabel = "Confirm", variant = "primary" } = {}) =>
      new Promise((resolve) => {
        setState({ title, message, confirmLabel, variant, resolve });
      }),
    []
  );

  const close = useCallback(
    (result) => {
      if (state?.resolve) state.resolve(result);
      setState(null);
    },
    [state]
  );

  // Focus confirm button on open
  useEffect(() => {
    if (state && confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, [state]);

  // Escape key
  useEffect(() => {
    if (!state) return;
    const handler = (e) => {
      if (e.key === "Escape") close(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [state, close]);

  // Trap focus inside modal
  useEffect(() => {
    if (!state) return;
    const trap = (e) => {
      if (e.key !== "Tab") return;
      const modal = backdropRef.current?.querySelector(".confirm-modal");
      if (!modal) return;
      const focusable = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trap);
    return () => document.removeEventListener("keydown", trap);
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}

      {state && (
        <div
          className="confirm-backdrop"
          ref={backdropRef}
          onClick={(e) => {
            if (e.target === e.currentTarget) close(false);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          aria-describedby="confirm-desc"
        >
          <div className="confirm-modal">
            <h3 id="confirm-title" className="confirm-title">
              {state.title}
            </h3>
            {state.message && (
              <p id="confirm-desc" className="confirm-message">
                {state.message}
              </p>
            )}
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => close(false)}>
                Cancel
              </button>
              <button
                ref={confirmBtnRef}
                className={`confirm-btn confirm-btn-${state.variant}`}
                onClick={() => close(true)}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
